import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { responseDocumentClient } from '../../lib/connect/clients';
export function containerKey(c) {
    return c.kind === 'rfi' ? `rfi:${c.rfiResponseProjectId}` : `volume:${c.projectId}:${c.volumeId}`;
}
export function toContainerRef(c) {
    return c.kind === 'rfi' ? {
        kind: 'rfi',
        rfiResponseProjectId: c.rfiResponseProjectId
    } : {
        kind: 'volume',
        projectId: c.projectId,
        volumeId: c.volumeId
    };
}
export const officeWorkspaceKey = (c, documentId)=>[
        'officeWorkspace',
        containerKey(c),
        documentId ?? ''
    ];
export const officeVersionsKey = (documentId)=>[
        'officeVersions',
        documentId ?? ''
    ];
/** WRT-04 §8a: poll every 10 s, only while the tab is visible. */ export const WORKSPACE_POLL_MS = 10000;
function visiblePollInterval() {
    if (typeof document === 'undefined') return false;
    return document.visibilityState === 'visible' ? WORKSPACE_POLL_MS : false;
}
/** `GetResponseWorkspace`, polled for the "updated, reload" banner. */ export function useResponseWorkspace(container, documentId) {
    return useQuery({
        queryKey: officeWorkspaceKey(container, documentId),
        queryFn: ()=>responseDocumentClient.getResponseWorkspace({
                container: toContainerRef(container),
                documentId: documentId ?? ''
            }),
        refetchInterval: visiblePollInterval,
        refetchOnWindowFocus: true
    });
}
export const VERSIONS_PAGE_SIZE = 30;
/** `ListDocumentVersions`, 30 per page, newest first ("Load older"). */ export function useDocumentVersions(container, documentId) {
    return useInfiniteQuery({
        queryKey: officeVersionsKey(documentId),
        enabled: !!documentId,
        initialPageParam: '',
        queryFn: ({ pageParam })=>responseDocumentClient.listDocumentVersions({
                container: toContainerRef(container),
                documentId: documentId ?? '',
                pageSize: VERSIONS_PAGE_SIZE,
                pageToken: pageParam
            }),
        getNextPageParam: (last)=>last.nextPageToken || undefined
    });
}
/** `GetVersionViewSession`: the read-only (mode view, `-ro` key, no callback) config. */ export function useVersionViewSession(container, documentId, versionId) {
    return useQuery({
        queryKey: [
            'officeVersionView',
            containerKey(container),
            documentId ?? '',
            versionId ?? ''
        ],
        enabled: !!documentId && !!versionId,
        queryFn: ()=>responseDocumentClient.getVersionViewSession({
                container: toContainerRef(container),
                documentId: documentId ?? '',
                versionId: versionId ?? ''
            }),
        // The config carries a short-lived JWT.
        staleTime: 0,
        gcTime: 0,
        refetchOnWindowFocus: false
    });
}
/** Every mutation invalidates the workspace and the version list (WRT-04 §9). */ export function useOfficeMutations(container) {
    const qc = useQueryClient();
    const ref = toContainerRef(container);
    const invalidate = ()=>Promise.all([
            qc.invalidateQueries({
                queryKey: [
                    'officeWorkspace',
                    containerKey(container)
                ]
            }),
            qc.invalidateQueries({
                queryKey: [
                    'officeVersions'
                ]
            })
        ]);
    const opts = {
        onSuccess: invalidate
    };
    return {
        labelVersion: useMutation({
            mutationFn: (v)=>responseDocumentClient.labelVersion({
                    container: ref,
                    ...v
                }),
            ...opts
        }),
        restoreVersion: useMutation({
            mutationFn: (v)=>responseDocumentClient.restoreDocumentVersion({
                    container: ref,
                    ...v
                }),
            ...opts
        }),
        createDraft: useMutation({
            mutationFn: (v)=>responseDocumentClient.createDraft({
                    container: ref,
                    ...v
                }),
            ...opts
        }),
        renameDraft: useMutation({
            mutationFn: (v)=>responseDocumentClient.renameDraft({
                    container: ref,
                    ...v
                }),
            ...opts
        }),
        promoteDraft: useMutation({
            mutationFn: (v)=>responseDocumentClient.promoteDraft({
                    container: ref,
                    ...v
                }),
            ...opts
        }),
        archiveDraft: useMutation({
            mutationFn: (v)=>responseDocumentClient.archiveDraft({
                    container: ref,
                    ...v
                }),
            ...opts
        }),
        importDocx: useMutation({
            mutationFn: (v)=>responseDocumentClient.importDocxVersion({
                    container: ref,
                    ...v
                }),
            ...opts
        }),
        downloadDocument: useMutation({
            // WRT-13: format is the document's own type (docx) or pdf.
            mutationFn: (v)=>responseDocumentClient.downloadDocument({
                    container: ref,
                    documentId: v.documentId,
                    format: v.format ?? 'docx',
                    stripComments: v.stripComments
                })
        }),
        // WRT-13: measure exact pages (ConvertService); the workspace refetch
        // then reads the stored count (basis "exact").
        checkExactPages: useMutation({
            mutationFn: (v)=>responseDocumentClient.getExactPageCount({
                    container: ref,
                    documentId: v.documentId
                }),
            ...opts
        })
    };
}
